import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Search,
  Gauge,
  Image as ImageIcon,
  Globe,
  Code2,
  Server,
  ShieldCheck,
  Rocket,
  Users,
  BarChart3,
  BadgeDollarSign,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { WhyUsSection } from "@/components/sections/WhyUsSection";

import type { GrowthItem } from "@/components/sections/WhyUsSection.data";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";

const siteSpeedGrowthItems: GrowthItem[] = [
  {
    title: "Visitors stop leaving",
    description: "A fast site keeps people from bouncing before the page even finishes loading.",
    Icon: Users,
    bgClass: "bg-[#3b82f6]/10",
    textClass: "text-[#3b82f6]",
  },
  {
    title: "Rankings climb",
    description: "Google rewards fast, stable pages with better visibility in search results.",
    Icon: BarChart3,
    bgClass: "bg-[#a855f7]/10",
    textClass: "text-[#a855f7]",
  },
  {
    title: "Ads convert better",
    description: "Faster landing pages improve quality score and lower your cost per conversion.",
    Icon: BadgeDollarSign,
    bgClass: "bg-[#ec4899]/10",
    textClass: "text-[#ec4899]",
  },
];

export const Route = createFileRoute("/site-speed")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Speed & Performance — Mind Media" },
      {
        name: "description",
        content:
          "We find what's slowing your site down and make it load fast, so you stop losing visitors, rankings, and ad money to a slow page.",
      },
      { property: "og:title", content: "Speed & Performance — Mind Media" },
      {
        property: "og:description",
        content:
          "A slow website quietly bleeds customers. We make it load fast, without breaking how it looks.",
      },
    ],
  }),
  component: SiteSpeedPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

function SiteSpeedPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-4 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            A slow website quietly <span className={gradText}>bleeds customers.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-4xl text-lg text-muted-foreground sm:text-xl">
            We find exactly what's dragging your site down and make it load fast, so you stop losing
            visitors, search rankings, and ad money to a page that takes too long.
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              Get a Free Speed Audit <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Pricing
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {["Core Web Vitals", "Faster means higher rankings", "Before & after report"].map(
              (p) => (
                <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                    <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                  </span>
                  {p}
                </span>
              ),
            )}
          </div>
        </div>
        <WhyUsSection
          onCtaClick={goToMockup}
          variant="hero"
          showTrustBar={false}
          showRealExperience={false}
          withDivider={false}
          shareloId="YPUxqANAOIfFQdRLyuxQC"
          shareloTitle="Speed — customer story"
          headline={
            <>
              <span className="block sm:inline">What a speed fix</span>{" "}
              <span className="block sm:inline">
                <span className="inline-flex items-baseline whitespace-nowrap sm:inline">
                  <span className="text-gradient-cycle align-baseline">does for you</span>
                </span>
              </span>
            </>
          }
          growthItems={siteSpeedGrowthItems}
        />
      </section>

      {/* PROBLEM */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="What slow really costs"
            maxWidth="max-w-6xl lg:max-w-none"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                Every second of load time <span className={gradText}>has a price tag.</span>
              </>
            }
          >
            A slow site doesn't just annoy people. It costs you on three fronts at once, and most
            owners never connect the dots.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Users,
                n: "Visitors",
                h: "Leave before it loads",
                p: "More than half of visitors abandon a page that takes over three seconds. Every one of them is a customer who never comes back.",
              },
              {
                icon: BarChart3,
                n: "Rankings",
                h: "Google buries you",
                p: "Site speed is part of how Google ranks pages. A slow site quietly drops below faster competitors in search.",
              },
              {
                icon: BadgeDollarSign,
                n: "Ad spend",
                h: "Costs you more per click",
                p: "Slow landing pages drag down your ad quality score, so you pay more for the same clicks. Speed pays you back twice.",
              },
            ].map((c) => (
              <div key={c.h} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <c.icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <div>
                    <div
                      className={cn(
                        "text-3xl tracking-tight md:text-4xl font-bold leading-tight text-brand-blue",
                      )}
                    >
                      {c.n}
                    </div>
                    <h3 className="mt-1 text-lg font-bold tracking-tight leading-snug">{c.h}</h3>
                  </div>
                </div>
                <p className="mt-4 text-[15px] text-muted-foreground">{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE FIX */}
      <section id="fix" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What we fix"
            title={
              <>
                Everything that's <span className={gradText}>slowing you down.</span>
              </>
            }
          >
            We go through your whole site, find the bottlenecks, and clear them out one by one.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Search,
                h: "Speed audit",
                p: "A full diagnosis of what's making your site slow, so we fix the real causes instead of guessing.",
              },
              {
                icon: Gauge,
                h: "Core Web Vitals",
                p: "We get you passing Google's speed and stability checks, the ones that actually move rankings.",
              },
              {
                icon: ImageIcon,
                h: "Image & media optimization",
                p: "Compression, lazy loading, and modern formats so heavy images stop choking your load time.",
              },
              {
                icon: Globe,
                h: "Caching & CDN",
                p: "We serve your pages from cache and a global network so they load fast wherever your visitors are.",
              },
              {
                icon: Code2,
                h: "Code & plugin cleanup",
                p: "We strip out bloat, render-blocking scripts, and heavy plugins that are quietly weighing the site down.",
              },
              {
                icon: Server,
                h: "Fast managed hosting",
                p: "Sometimes the host is the bottleneck. We can move you to managed hosting built for speed, if that's what's holding you back.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="Why us"
            title={
              <span className="lg:whitespace-nowrap">
                Fast, without <span className={gradText}>breaking how it looks.</span>
              </span>
            }
          >
            Plenty of plugins promise speed and quietly wreck your layout doing it. We're a design
            studio, so we make your site fast and keep it looking exactly the way it should. Speed
            and good design aren't a trade-off when someone who cares about both is doing the work.
          </SectionHead>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <ul className="space-y-5">
              {[
                [
                  "We find the real cause",
                  "Not a one-click plugin and a prayer. We diagnose what's actually slow.",
                ],
                [
                  "Design stays intact",
                  "Faster site, same look. Nothing breaks in the name of a score.",
                ],
                [
                  "Proof, before and after",
                  "You see your real load times and scores go up. No vague promises.",
                ],
                [
                  "It can stay fast",
                  "Add monitoring so it doesn't slowly creep back up over time.",
                ],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Check className="h-4 w-4 text-brand-blue" strokeWidth={3} />
                  </span>
                  <div>
                    <strong className="block text-base font-bold">{title}</strong>
                    <span className="text-[15px] text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <div className="text-6xl tracking-tight leading-none font-bold text-white">
                3 seconds
              </div>
              <div className="mt-2 text-lg font-bold">is all the patience you get</div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                More than half of visitors leave a site that takes longer than three seconds to
                load, and on mobile that's where most of your traffic is. Shave those seconds off
                and you're not just faster, you're keeping customers a slow site was handing to the
                competition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="px-6 pt-20 pb-6">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <span className="lg:whitespace-nowrap">
                Slow to fast in <span className={gradText}>four steps.</span>
              </span>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: 1,
                h: "Free audit",
                p: "Send your URL and we'll show you how slow it really is and exactly what's causing it.",
              },
              {
                n: 2,
                h: "Diagnose",
                p: "We map out every bottleneck, from heavy images to bloated code to the host itself, and plan the fixes.",
              },
              {
                n: 3,
                h: "Optimize",
                p: "We work through the list, testing as we go, so the site gets faster without anything breaking.",
              },
              {
                n: 4,
                h: "Verify",
                p: "We hand you a clear before-and-after report so you can see the load times and scores for yourself.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn("text-5xl leading-none tracking-tight font-bold text-brand-blue")}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection
        className="pt-4 sm:pt-6 pb-4 sm:pb-6"
        contentClassName="mt-0"
        hideMockupButton
        meetingButtonPrimary
        hideKicker
        centerContent
      />

      {/* PRICING */}
      <section id="pricing" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Pricing"
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                Pick how fast you want <span className={gradText}>to go.</span>
              </>
            }
          >
            Start with a one-time tune-up, or keep it fast for good.
          </SectionHead>
          <div className="grid items-stretch gap-6 md:grid-cols-3">
            {[
              {
                h: "Speed Tune-Up",
                desc: "One-time optimization",
                amt: "$499",
                per: "",
                sub: "A full overhaul, done once",
                b: [
                  "Full speed audit",
                  "Core Web Vitals fixes",
                  "Images, caching & code cleanup",
                  "Before & after report",
                ],
                cta: "ghost",
                feat: false,
              },
              {
                h: "Speed + Care",
                desc: "Get fast, stay fast",
                amt: "$149",
                per: "/mo",
                sub: "Tune-up plus ongoing monitoring",
                b: [
                  "Everything in the Tune-Up",
                  "Ongoing speed monitoring",
                  "Re-optimization as it grows",
                  "Folds into a Care Plan",
                ],
                cta: "primary",
                feat: true,
              },
              {
                h: "Speed + Hosting",
                desc: "When the host is the problem",
                amt: "Let's talk",
                per: "",
                sub: "Tune-up plus a move to fast hosting",
                b: [
                  "Everything in Speed + Care",
                  "Migration to managed hosting",
                  "Hosting built for WordPress speed",
                  "We handle the whole move",
                ],
                cta: "ghost",
                feat: false,
              },
            ].map((p) => (
              <div
                key={p.h}
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-card p-8",
                  p.feat
                    ? "border-transparent shadow-2xl shadow-purple-500/20 ring-2 ring-transparent [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(100deg,oklch(0.585_0.196_263),#a855f7,#ec4899)_border-box]"
                    : "border-border",
                )}
              >
                {p.feat && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-bold tracking-tight">{p.h}</h3>
                <div className="mt-1 text-sm text-muted-foreground">{p.desc}</div>
                <div className="mt-5 text-4xl font-extrabold tracking-tight">
                  {p.amt}
                  {p.per && (
                    <small className="ml-1 text-sm font-semibold text-muted-foreground">
                      {p.per}
                    </small>
                  )}
                </div>
                <div className="mt-1 text-xs font-medium text-muted-foreground">{p.sub}</div>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToMockup}
                  className={cn(
                    "mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5",
                    p.cta === "primary"
                      ? "bg-gradient-to-r from-brand-blue to-[#ec4899] text-white shadow-lg shadow-brand-blue/40 hover:opacity-90"
                      : "border-2 border-brand-blue bg-background text-brand-blue hover:bg-brand-blue/10",
                  )}
                >
                  Get started
                </button>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Not sure which you need? Start with the free audit and we'll tell you straight.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Questions"
            title={
              <>
                What people ask <span className={gradText}>about speed.</span>
              </>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "How do I know my site is too slow?",
                "If it takes more than two or three seconds to load, it's costing you. The free audit gives you the real numbers and shows exactly where the time is going.",
              ],
              [
                "How much faster will it actually get?",
                "It depends where you're starting, but most sites see a big jump. We show you the before-and-after numbers so the improvement is something you can see, not just take our word for.",
              ],
              [
                "Will optimizing break my site?",
                "No. We test as we go and keep a backup, so the site gets faster while looking and working exactly as it did. Speed shouldn't cost you your design.",
              ],
              [
                "How long does it take?",
                "A one-time tune-up is usually a matter of days, depending on how much is going on under the hood. We'll give you a clear timeline after the audit.",
              ],
              [
                "Do I have to switch hosts?",
                "Only if your host is the thing holding you back, and we'll tell you honestly if it is. If so, we can move you to fast managed hosting and handle the whole migration for you.",
              ],
              [
                "Does this help my SEO and ads too?",
                "Yes. A faster site ranks better on Google and improves your ad quality score, so the same speed work pays off across search and paid traffic, not just the page itself.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <ContactSection
        compact
        heading={
          <h2 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl">
            Find out how slow <br className="hidden lg:block" />
            <span className={gradText}>you really are.</span>
          </h2>
        }
        subheading={
          <p className="mt-4 text-lg text-muted-foreground">
            Send us your website and we'll run a free speed audit, with the real numbers and exactly
            what's dragging it down. No strings.
          </p>
        }
        ctaText="Get my free speed audit"
        valueProps={[
          {
            Icon: Gauge,
            title: "Free speed audit",
            desc: "Real numbers on what's slowing your site down.",
          },
          {
            Icon: Search,
            title: "Exact bottlenecks",
            desc: "We pinpoint the fixes that matter most.",
          },
          {
            Icon: ShieldCheck,
            title: "No strings attached",
            desc: "Just a clear report. No pressure, no pitch.",
          },
        ]}
        stats={[
          { Icon: Gauge, value: "3 sec", label: "patience limit" },
          { Icon: Check, value: "Passing", label: "Core Web Vitals" },
          { Icon: Rocket, value: "2x", label: "typical speed lift" },
        ]}
      />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/site-speed", label: "Speed" },
          { href: "/care-plans", label: "Care Plans" },
          { href: "/seo", label: "SEO" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
  childrenClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
  childrenClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && (
        <p className={cn("mt-4 text-lg leading-relaxed text-muted-foreground", childrenClassName)}>
          {children}
        </p>
      )}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
